// src/pages/StockTransfersPage.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Package, Truck, CheckCircle, Clock, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { stockTransferApi, storeApi, productApi } from '../api';

const STATUS_CONFIG = {
  pending:   { label: '待发货', color: 'bg-yellow-100 text-yellow-700' },
  shipped:   { label: '已发货', color: 'bg-blue-100 text-blue-700' },
  completed: { label: '已完成', color: 'bg-green-100 text-green-700' },
  cancelled: { label: '已取消', color: 'bg-gray-100 text-gray-500' }
};

const StockTransfersPage = () => {
  const { currentUser } = useApp();
  const isHQ = currentUser?.store_type === 'headquarters';
  const isManager = currentUser?.role === 'manager';

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedNo, setExpandedNo] = useState(null);
  const [detailMap, setDetailMap] = useState({});

  // 创建划拨单
  const [showCreate, setShowCreate] = useState(false);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [createForm, setCreateForm] = useState({ to_store_id: '', notes: '', items: [] });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // 收货弹窗
  const [showReceive, setShowReceive] = useState(false);
  const [receiveTransferNo, setReceiveTransferNo] = useState('');
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [receiving, setReceiving] = useState(false);

  useEffect(() => {
    loadTransfers();
    if (isHQ) {
      loadStores();
      loadProducts();
    }
  }, [filterStatus]);

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const res = await stockTransferApi.getList(filterStatus ? { status: filterStatus } : {});
      if (res.success) setTransfers(res.data);
    } catch (err) {
      console.error('加载划拨单失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStores = async () => {
    try {
      const res = await storeApi.getList();
      if (res.success) setStores(res.data.filter(s => s.type === 'branch'));
    } catch (err) { console.error(err); }
  };

  const loadProducts = async () => {
    try {
      const res = await productApi.getList({ status: 'active' });
      if (res.success) setProducts(res.data);
    } catch (err) { console.error(err); }
  };

  const toggleDetail = async (transfer_no) => {
    if (expandedNo === transfer_no) {
      setExpandedNo(null);
      return;
    }
    setExpandedNo(transfer_no);
    if (!detailMap[transfer_no]) {
      try {
        const res = await stockTransferApi.getDetail(transfer_no);
        if (res.success) setDetailMap(prev => ({ ...prev, [transfer_no]: res.data }));
      } catch (err) { console.error(err); }
    }
  };

  // 创建划拨单
  const addItem = () => {
    setCreateForm(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', quantity: 1, product_name: '', stock: 0 }]
    }));
  };

  const removeItem = (idx) => {
    setCreateForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const updateItem = (idx, field, value) => {
    setCreateForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'product_id') {
        const product = products.find(p => p.id === parseInt(value));
        if (product) {
          items[idx].product_name = product.name;
          items[idx].stock = product.stock;
        }
      }
      return { ...prev, items };
    });
  };

  const handleCreate = async () => {
    if (!createForm.to_store_id) { setCreateError('请选择目标分店'); return; }
    if (createForm.items.length === 0) { setCreateError('请至少添加一个商品'); return; }
    for (const item of createForm.items) {
      if (!item.product_id) { setCreateError('请选择商品'); return; }
      if (item.quantity < 1) { setCreateError('数量必须大于0'); return; }
      if (item.quantity > item.stock) { setCreateError(`商品「${item.product_name}」库存不足`); return; }
    }

    setCreating(true);
    setCreateError('');
    try {
      const res = await stockTransferApi.create({
        to_store_id: parseInt(createForm.to_store_id),
        notes: createForm.notes,
        items: createForm.items.map(i => ({ product_id: parseInt(i.product_id), quantity: parseInt(i.quantity) }))
      });
      if (res.success) {
        setShowCreate(false);
        setCreateForm({ to_store_id: '', notes: '', items: [] });
        loadTransfers();
      } else {
        setCreateError(res.message || '创建失败');
      }
    } catch (err) {
      setCreateError(err.response?.data?.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  // 发货
  const handleShip = async (transfer_no) => {
    if (!window.confirm(`确认发货划拨单 ${transfer_no}？发货后将扣减总店库存。`)) return;
    try {
      const res = await stockTransferApi.ship(transfer_no);
      if (res.success) {
        alert('发货成功');
        loadTransfers();
        setDetailMap(prev => { const n = { ...prev }; delete n[transfer_no]; return n; });
      } else {
        alert(res.message || '发货失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '发货失败');
    }
  };

  // 收货弹窗
  const openReceive = async (transfer_no) => {
    setReceiveTransferNo(transfer_no);
    setReceiveNotes('');
    try {
      const res = await stockTransferApi.getDetail(transfer_no);
      if (res.success) {
        setReceiveItems(res.data.map(t => ({ transfer_id: t.id, product_name: t.product_name, quantity: t.quantity, actual_quantity: t.quantity })));
      }
    } catch (err) { console.error(err); }
    setShowReceive(true);
  };

  const handleReceive = async () => {
    setReceiving(true);
    try {
      const res = await stockTransferApi.receive(receiveTransferNo, {
        received_items: receiveItems.map(i => ({ transfer_id: i.transfer_id, actual_quantity: parseInt(i.actual_quantity) })),
        notes: receiveNotes
      });
      if (res.success) {
        alert('收货确认成功，库存已更新');
        setShowReceive(false);
        loadTransfers();
        setDetailMap(prev => { const n = { ...prev }; delete n[receiveTransferNo]; return n; });
      } else {
        alert(res.message || '收货失败');
      }
    } catch (err) {
      alert(err.response?.data?.message || '收货失败');
    } finally {
      setReceiving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">库存划拨</h2>
          <p className="text-gray-600 mt-1">
            {isHQ ? '总店向分店划拨商品库存' : '查看本店收到的库存划拨'}
          </p>
        </div>
        {isHQ && (
          <button
            onClick={() => { setShowCreate(true); setCreateError(''); }}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新建划拨单</span>
          </button>
        )}
      </div>

      {/* 状态筛选 */}
      <div className="flex space-x-2">
        {[['', '全部'], ['pending', '待发货'], ['shipped', '已发货'], ['completed', '已完成']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilterStatus(val)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filterStatus === val
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 划拨单列表 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">加载中...</p>
        </div>
      ) : transfers.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无划拨记录</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transfers.map(transfer => {
            const status = STATUS_CONFIG[transfer.status] || STATUS_CONFIG.pending;
            const isExpanded = expandedNo === transfer.transfer_no;
            const details = detailMap[transfer.transfer_no];

            return (
              <div key={transfer.transfer_no} className="bg-white rounded-xl shadow overflow-hidden">
                {/* 划拨单头部 */}
                <div
                  className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleDetail(transfer.transfer_no)}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold">{transfer.transfer_no}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {transfer.from_store_name} → {transfer.to_store_name} ·
                        共 {transfer.item_count} 种商品 ·
                        {new Date(transfer.created_at).toLocaleDateString('zh-CN')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {/* 操作按钮 */}
                    {isHQ && transfer.status === 'pending' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleShip(transfer.transfer_no); }}
                        className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                      >
                        <Truck className="w-4 h-4" />
                        <span>确认发货</span>
                      </button>
                    )}
                    {isManager && transfer.status === 'shipped' && transfer.to_store_id === currentUser?.store_id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openReceive(transfer.transfer_no); }}
                        className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700 transition-colors"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>确认收货</span>
                      </button>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {/* 展开明细 */}
                {isExpanded && (
                  <div className="border-t px-5 py-4 bg-gray-50">
                    {details ? (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="text-left py-1">商品名称</th>
                            <th className="text-right py-1">划拨数量</th>
                            <th className="text-right py-1">成本价</th>
                            <th className="text-right py-1">小计</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.map(item => (
                            <tr key={item.id} className="border-t border-gray-100">
                              <td className="py-2">{item.product_name}</td>
                              <td className="text-right py-2">{item.quantity}</td>
                              <td className="text-right py-2">${parseFloat(item.cost_price).toFixed(2)}</td>
                              <td className="text-right py-2">${parseFloat(item.total_amount).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-gray-200 font-semibold">
                            <td className="py-2">合计</td>
                            <td className="text-right py-2">{details.reduce((s, i) => s + i.quantity, 0)}</td>
                            <td></td>
                            <td className="text-right py-2">
                              ${details.reduce((s, i) => s + parseFloat(i.total_amount), 0).toFixed(2)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      <p className="text-center text-gray-400 py-4">加载中...</p>
                    )}
                    {transfer.notes && (
                      <p className="text-sm text-gray-500 mt-3">备注：{transfer.notes}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 创建划拨单弹窗 */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">新建划拨单</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              {/* 目标分店 */}
              <div>
                <label className="block text-sm font-medium mb-1">目标分店 *</label>
                <select
                  value={createForm.to_store_id}
                  onChange={e => setCreateForm(prev => ({ ...prev, to_store_id: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">请选择分店</option>
                  {stores.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* 商品列表 */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium">划拨商品 *</label>
                  <button
                    onClick={addItem}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>添加商品</span>
                  </button>
                </div>

                {createForm.items.length === 0 ? (
                  <div
                    onClick={addItem}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                  >
                    <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500 text-sm">点击添加商品</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {createForm.items.map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-2 bg-gray-50 p-3 rounded-lg">
                        <select
                          value={item.product_id}
                          onChange={e => updateItem(idx, 'product_id', e.target.value)}
                          className="flex-1 px-2 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">选择商品</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name}（库存：{p.stock}）
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="1"
                          max={item.stock}
                          value={item.quantity}
                          onChange={e => updateItem(idx, 'quantity', e.target.value)}
                          className="w-24 px-2 py-1.5 border rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500"
                          placeholder="数量"
                        />
                        <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 备注 */}
              <div>
                <label className="block text-sm font-medium mb-1">备注</label>
                <textarea
                  value={createForm.notes}
                  onChange={e => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={2}
                  placeholder="备注信息（可选）"
                />
              </div>

              {createError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {createError}
                </div>
              )}
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors ${creating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
              >
                {creating ? '提交中...' : '创建划拨单'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 收货确认弹窗 */}
      {showReceive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">确认收货</h3>
              <button onClick={() => setShowReceive(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">请核对实际收到的数量，如有差异请修改：</p>

              <div className="space-y-2">
                {receiveItems.map((item, idx) => (
                  <div key={item.transfer_id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.product_name}</p>
                      <p className="text-xs text-gray-500">应收数量：{item.quantity}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">实收：</span>
                      <input
                        type="number"
                        min="0"
                        max={item.quantity}
                        value={item.actual_quantity}
                        onChange={e => {
                          const newItems = [...receiveItems];
                          newItems[idx].actual_quantity = e.target.value;
                          setReceiveItems(newItems);
                        }}
                        className="w-20 px-2 py-1 border rounded-lg text-sm text-center focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">收货备注</label>
                <textarea
                  value={receiveNotes}
                  onChange={e => setReceiveNotes(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                  rows={2}
                  placeholder="如有差异请说明原因（可选）"
                />
              </div>
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => setShowReceive(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleReceive}
                disabled={receiving}
                className={`flex-1 px-4 py-2 bg-green-600 text-white rounded-lg transition-colors ${receiving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'}`}
              >
                {receiving ? '确认中...' : '确认收货'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTransfersPage;